import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { usePanelRef } from 'react-resizable-panels';

import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/_shared/components/ui/resizable';
import { cn } from '@/_shared/lib/utils';
import { PREVIEW_PANEL_SLIDE_MS } from '@/chat/hooks/usePreviewPanelSlide';

const ARTIFACT_PANEL_DEFAULT_VW = 0.4;
const ARTIFACT_PANEL_DEFAULT_MAX_PX = 600;
const ARTIFACT_PANEL_MIN_PX = 280;
const ARTIFACT_PANEL_MAX_PX = 720;
/** Fallback when `window` is unavailable (SSR / non-browser test hosts). */
const ARTIFACT_PANEL_SSR_VIEWPORT_PX = 1024;
/** Bumped when default sizing changes so persisted layouts don't stick on the old width. */
const ARTIFACT_PANEL_LAYOUT_ID = 'chat-dashboard-artifact-v3';

// eslint-disable-next-line react-refresh/only-export-components -- unit-tested layout helper co-located with panel shell
export function artifactPanelTargetWidthPx(viewportWidth: number): number {
  return Math.min(viewportWidth * ARTIFACT_PANEL_DEFAULT_VW, ARTIFACT_PANEL_DEFAULT_MAX_PX);
}

function readViewportWidthPx(): number {
  return typeof window !== 'undefined' ? window.innerWidth : ARTIFACT_PANEL_SSR_VIEWPORT_PX;
}

interface DashboardPreviewSlideShellProps {
  shown: boolean;
  transcript: React.ReactNode;
  children: React.ReactNode;
  /** Called when the user drags the panel below minSize (library collapses to 0). */
  onClose?: () => void;
}

/** Desktop split: transcript + resizable preview column with width + slide transition. */
export function DashboardPreviewSlideShell({
  shown,
  transcript,
  children,
  onClose,
}: DashboardPreviewSlideShellProps): React.ReactElement {
  const previewPanelRef = usePanelRef();
  const wasShownRef = useRef(shown);
  const shownRef = useRef(shown);
  const onCloseRef = useRef(onClose);
  // Recompute when `shown` flips so a pre-open viewport resize is picked up (defaultSize is mount-time).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `shown` triggers re-read of viewport width
  const defaultWidthPx = useMemo(() => artifactPanelTargetWidthPx(readViewportWidthPx()), [shown]);

  useLayoutEffect(() => {
    shownRef.current = shown;
  }, [shown]);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // CSS alone cannot collapse a persisted resizable panel — drive it imperatively so
  // closing the artifact returns the transcript to full width without unmounting it.
  // Never collapse on the initial mount (shown=false before the slide-in rAF) — that
  // poisons expandToSize with a tiny width and expand() reopens at ~200px.
  // Defer collapse until after the slide-out so translate-x can animate.
  useLayoutEffect(() => {
    const panel = previewPanelRef.current;
    if (!panel) return undefined;

    const applyOpenSize = (): void => {
      const current = previewPanelRef.current;
      if (!current) return;
      const target = artifactPanelTargetWidthPx(readViewportWidthPx());
      if (current.isCollapsed()) {
        current.expand();
      }
      // Opening after a viewport resize (or first open) — snap to current default.
      // Persisted layout + mount-time defaultSize can otherwise leave a stale width.
      if (!wasShownRef.current) {
        current.resize(target);
        return;
      }
      const { inPixels } = current.getSize();
      if (inPixels < ARTIFACT_PANEL_MIN_PX) {
        current.resize(target);
      }
    };

    if (shown) {
      applyOpenSize();
      const outerId = requestAnimationFrame(() => {
        applyOpenSize();
      });
      wasShownRef.current = shown;
      return () => cancelAnimationFrame(outerId);
    }

    if (wasShownRef.current) {
      const closeTimer = window.setTimeout(() => {
        previewPanelRef.current?.collapse();
      }, PREVIEW_PANEL_SLIDE_MS);
      wasShownRef.current = shown;
      return () => clearTimeout(closeTimer);
    }

    wasShownRef.current = shown;
    return undefined;
  }, [shown, previewPanelRef]);

  // collapsible + minSize: dragging below min collapses to 0. Treat that as dismiss.
  const handlePreviewResize = useCallback(
    (size: { inPixels: number }, _id: string | number | undefined, prev: { inPixels: number } | undefined) => {
      if (!shownRef.current || prev === undefined) return;
      if (size.inPixels > 0 && !previewPanelRef.current?.isCollapsed()) return;
      onCloseRef.current?.();
    },
    [previewPanelRef],
  );

  return (
    <ResizablePanelGroup direction="horizontal" autoSaveId={ARTIFACT_PANEL_LAYOUT_ID} className="min-h-0 flex-1">
      <ResizablePanel id="chat-transcript" minSize="25%">
        <div className="flex h-full min-h-0 flex-col overflow-hidden">{transcript}</div>
      </ResizablePanel>
      <ResizableHandle withHandle className={cn(!shown && 'hidden')} aria-label="Resize dashboard preview" />
      <ResizablePanel
        id="chat-dashboard-preview"
        panelRef={previewPanelRef}
        defaultSize={`${defaultWidthPx}px`}
        minSize={`${ARTIFACT_PANEL_MIN_PX}px`}
        maxSize={`${ARTIFACT_PANEL_MAX_PX}px`}
        collapsible
        collapsedSize={0}
        onResize={handlePreviewResize}
        className="overflow-hidden"
      >
        <div
          className={cn(
            'flex h-full w-full flex-col bg-background motion-safe:transition-transform motion-safe:ease-out',
            shown ? 'translate-x-0' : 'translate-x-full',
            'motion-reduce:transition-none',
          )}
          style={{ transitionDuration: `${PREVIEW_PANEL_SLIDE_MS}ms` }}
        >
          {children}
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
